import { useEffect } from "react";

export type ToastVariant = "error" | "success" | "info";

export interface ToastMessage {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastItemProps {
  toast: ToastMessage;
  onDismiss: (id: number) => void;
}

const COLORS: Record<ToastVariant, { bg: string; border: string; icon: string }> = {
  error:   { bg: "oklch(0.98 0.02 25)", border: "oklch(0.62 0.20 25)", icon: "⚠" },
  success: { bg: "oklch(0.97 0.04 130)", border: "var(--accent)", icon: "✓" },
  info:    { bg: "var(--bg-3)", border: "var(--line)", icon: "ℹ" },
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const c = COLORS[toast.variant];

  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), 5000);
    return () => clearTimeout(t);
  }, [toast.id, onDismiss]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 14px",
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        maxWidth: 420,
        animation: "toast-in 0.18s ease",
      }}
    >
      <span style={{ flexShrink: 0, fontSize: 14 }}>{c.icon}</span>
      <span style={{ fontSize: 13, lineHeight: 1.4, flex: 1 }}>{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "0 2px",
          color: "var(--muted)",
          fontSize: 14,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: number) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;
  return (
    <>
      <style>{`@keyframes toast-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }`}</style>
      <div
        style={{
          position: "fixed",
          bottom: 28,
          right: 20,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: "auto" }}>
            <ToastItem toast={t} onDismiss={onDismiss} />
          </div>
        ))}
      </div>
    </>
  );
}
